import { useEffect, useState } from 'react';

export const PULL_THRESHOLD_PX = 70;
const MAX_PULL_PX = 110;
const RESISTANCE = 0.5;

export type PullToRefreshState = {
  pullDistance: number;
  isRefreshing: boolean;
};

export function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean })
    .standalone;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    iosStandalone === true
  );
}

function isScrollLocked(): boolean {
  return window.getComputedStyle(document.body).overflowY === 'hidden';
}

function isInsideScrolledElement(target: EventTarget | null): boolean {
  let el = target instanceof Element ? target : null;
  while (el && el !== document.body && el !== document.documentElement) {
    if (el.scrollTop > 0) return true;
    el = el.parentElement;
  }
  return false;
}

const reloadPage = () => window.location.reload();

export function usePullToRefresh(
  enabled: boolean,
  onRefresh: () => void = reloadPage,
): PullToRefreshState {
  const [state, setState] = useState<PullToRefreshState>({
    pullDistance: 0,
    isRefreshing: false,
  });

  useEffect(() => {
    if (!enabled || !isStandaloneDisplay()) return undefined;

    let startY: number | null = null;
    let distance = 0;

    const reset = () => {
      startY = null;
      distance = 0;
      setState((prev) =>
        prev.pullDistance === 0 ? prev : { ...prev, pullDistance: 0 },
      );
    };

    const onTouchStart = (e: TouchEvent) => {
      if (
        e.touches.length !== 1 ||
        window.scrollY > 0 ||
        isScrollLocked() ||
        isInsideScrolledElement(e.target)
      ) {
        startY = null;
        return;
      }
      startY = e.touches[0].clientY;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (startY === null) return;
      if (window.scrollY > 0) {
        reset();
        return;
      }
      const delta = e.touches[0].clientY - startY;
      distance = delta > 0 ? Math.min(delta * RESISTANCE, MAX_PULL_PX) : 0;
      setState((prev) =>
        prev.pullDistance === distance
          ? prev
          : { ...prev, pullDistance: distance },
      );
    };

    const onTouchEnd = () => {
      if (startY === null) return;
      if (distance >= PULL_THRESHOLD_PX) {
        startY = null;
        setState({ pullDistance: PULL_THRESHOLD_PX, isRefreshing: true });
        onRefresh();
        return;
      }
      reset();
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('touchcancel', reset);

    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', reset);
    };
  }, [enabled, onRefresh]);

  return state;
}
